import { Checkbox, Form, Select, Spin, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ProColumns } from '@ant-design/pro-components';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomBreadcrumb from 'base/ui/components/CustomBreadcrumb';
import CustomProTable from 'base/ui/components/CustomTable';
import { usePermission } from 'hooks/usePermission';
import { cloneDeep } from 'lodash';
import { observer } from 'mobx-react';
import { AccessMobileModel, IAccessMobileServices, MobileScreenAccessResponse } from 'services/RawData/accessMobile_services';
import { commonMasterDataPresenter, commonMasterDataStore } from 'stores/root_store';
import { SYSTEM_TITLE } from 'utils/constants';

import { SaveFilled } from '@ant-design/icons';
import CustomInput from 'base/ui/components/CustomInput';
import CustomButtonIcon from 'base/ui/components/Button/buttonIcon';
import { useTranslation } from 'react-i18next';

const accessMobilePage = (accessMobileServices: IAccessMobileServices) => {
  return observer(() => {
    const pagetitle = 'Phân quyền chức năng mobile';
    document.title = pagetitle + ' - ' + SYSTEM_TITLE;
    usePermission('INDEX'); // Kiểm tra xem được quyền truy cập vào trang
    const [form] = Form.useForm();

    // draft params cache (gõ không re-render)
    const paramsDraftRef = useRef<Record<string, string>>({});

    // State để quản lý params của từng screen (để input controlled và re-render đúng)
    const [paramsMap, setParamsMap] = useState<Record<string, string>>({});

    const { t } = useTranslation();


    // ========================USE STATE================================================================
    // Loading all page
    const [loading, setLoading] = useState(false);

    // roleId để làm key đổi role
    const roleId = Form.useWatch('role', form);


    // version để ép remount theo lần fetch (tránh “chậm 1 nhịp”)
    const [dataVersion, setDataVersion] = useState(0);

    // Columns table title
    const defaultColumn: ProColumns<MobileScreenAccessResponse>[] = useMemo(
      () => [
        {
          title: 'STT',
          dataIndex: 'index',
          valueType: 'index',
          key: 'stt',
          width: 40,
          align: 'center',
        },
        {
          title: 'Màn hình',
          dataIndex: 'screenName',
          key: 'screenName',
          width: 180,
        },
        {
          title: 'Thao tác',
          dataIndex: 'thaotac',
          key: 'thaotac',
          width: 300,
          render: (_, record) => (
            <div className="flex items-center gap-4">
              {record.functionAccessResponses.map((item) => (
                <div key={item?.functionId} className='mr-2'>
                  <Checkbox
                    key={item.functionId}
                    checked={item.selected}
                    value={item.functionId}
                    onChange={(e) => {
                      handleCheckFunction(item.functionId, record.mobileScreenId, e.target.checked);
                    }}
                  >
                    {item.functionName}
                  </Checkbox>
                </div>
              ))}
            </div>
          ),
        },

        // NEW: Parameter column
        {
          title: 'Tham số',
          dataIndex: 'params',
          key: 'params',
          width: 120,
          render: (_, record) => {
            const k = `${record.mobileScreenId}`;
            const initVal = paramsDraftRef.current[k] ?? ((record as any).params ?? '');

            return (
              <div className="flex items-center gap-x-2">
                <div className="flex-1 min-w-0">
                  <CustomInput
                    // remount khi đổi role / fetch xong => defaultValue ăn đúng, không bị trễ 1 nhịp
                    key={`${roleId ?? 'no-role'}-${dataVersion}-${record.mobileScreenId}`}
                    defaultValue={initVal}
                    className="w-full"
                    onChange={(e: any) => {
                      const v = e?.target?.value ?? e ?? '';
                      paramsDraftRef.current[k] = v;
                    }}
                    onBlur={(e: any) => {
                      const v = e?.target?.value ?? e ?? '';
                      paramsDraftRef.current[k] = v;
                    }}
                  />
                </div>

                <CustomButtonIcon
                  color="#008000"
                  className="p-[5px] shrink-0"
                  onClick={() => {
                    const val = paramsDraftRef.current[k] ?? '';
                    handleCheckParams(record.mobileScreenId, val);
                  }}
                >
                  <SaveFilled className="!text-[20px] !text-[#008000]" />
                </CustomButtonIcon>
              </div>
            );
          },
        },
      ],
      [t, roleId, dataVersion],
    );
    // Custom default columns table with resizeable
    const [columns, setColumn] = useState<ProColumns<MobileScreenAccessResponse>[]>(defaultColumn);
    const [dataAccess, setDataAccess] = useState<AccessMobileModel[]>([])

    // Sync columns với defaultColumn khi role hoặc dataVersion thay đổi
    useEffect(() => {
      setColumn(defaultColumn);
    }, [defaultColumn]);

    const handleCheckParams = async (mobileScreenId: string, params: string) => {
      try {
        const result = await accessMobileServices.updateParams({
          params,
          rolesId: form.getFieldValue('role'),
          mobileScreenId,
        });
        if (result.isSuccess) {
          // Sau khi save thành công, fetch lại data để đảm bảo đồng bộ
          getAccessFromRole(form.getFieldValue('role'));
        }
      } catch (error) { }
    };

    // ========================FUNCTION ================================================================
    const handleResize =
      (index: number) =>
        (e: any, { size }: any) => {
          // Set columns
          setColumn((prevColumns) => {
            const nextColumns = [...prevColumns];
            nextColumns[index] = {
              ...nextColumns[index],
              width: size.width,
            };
            return nextColumns;
          });
        };
    const getListRoles = async () => {
      if (!commonMasterDataStore.listRoles.length) {
        await commonMasterDataPresenter.getDropdownRole(commonMasterDataStore)
      }
      form.setFieldValue('role', commonMasterDataStore.listRoles[0].key)
      getAccessFromRole(commonMasterDataStore.listRoles[0].key)
    }

    const getAccessFromRole = async (roleId: string) => {
      try {
        setLoading(true);
        const result = await accessMobileServices.getAccessMobileByRoleId(roleId)
        setLoading(false);
        if (result.isSuccess) {
          const dataClone = cloneDeep(result.data)
          const dataFilter = dataClone.filter(item => item.menuName === 'Màn hình mobile')

          // Cập nhật paramsMap với data mới từ API
          const newParamsMap: Record<string, string> = {};
          dataFilter.forEach(item => {
            item.mobileScreenAccessResponses.forEach((screen: any) => {
              newParamsMap[screen.mobileScreenId] = screen.params ?? '';
            });
          });
          // clear any existing drafts (we got fresh data from server)
          paramsDraftRef.current = {};
          setParamsMap(newParamsMap);

          // bump version to force remount inputs that use defaultValue
          setDataVersion(v => v + 1);

          // Set data cuối cùng
          setDataAccess(dataFilter)
        } else {
          message.error(result.message)
        }
      } catch (error) {
        console.log(error);
      }
    }
    const handleCheckFunction = async (functionId: string, pageId: string, checked: boolean) => {
      try {
        const result = await accessMobileServices.updateFunctionPermissions({ check: checked, functionId: functionId, roleId: form.getFieldValue('role'), mobileScreenId: pageId })
        if (result.isSuccess) {
          getAccessFromRole(form.getFieldValue('role'))
        }
      } catch (error) {
        console.log(error);
      }
    }
    // scroll to top
    useEffect(() => {
      window.scrollTo(0, 0);
      getListRoles()
    }, []);


    return (
      <div className="w-full">
        {loading && <div className='fixed w-screen h-screen bg-black/20 top-0 z-50 flex justify-center items-center'> <Spin></Spin></div>}
        {/* ==================================== Breadcrumb ========================================= */}
        <CustomBreadcrumb pagetitle={[{ title: pagetitle, link: '/MasterData/Access' }]} />

        {/* Tables */}
        <BoxContainer>
          <Form colon={false} form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 14 }}  >
            <Form.Item name={'role'} label={<h3 className='font-medium'>Nhóm người dùng</h3>}>
              <Select onChange={(value, option) => {
                getAccessFromRole(value)
              }} options={commonMasterDataStore.listRoles} fieldNames={{ label: 'value', value: 'key' }} />
            </Form.Item>
          </Form>
          {dataAccess.map((item, index) => {
            return (
              <div key={item.menuId} className='border border-primary/90 mb-4 rounded-t-[4px]'>
                <div className='text-white text-sm w-full bg-primary/90 px-3 py-2 rounded-t-sm'>
                  {item.menuName}
                </div>
                <CustomProTable<MobileScreenAccessResponse>
                  className='w-full'
                  columns={columns}
                  handleResize={handleResize}
                  rowKey="pageId"
                  size="small"
                  bordered
                  scroll={{ x: undefined }}
                  search={false}
                  options={false}
                  // loading={loading}
                  dataSource={item.mobileScreenAccessResponses}
                  pagination={false}

                />
              </div>
            );
          })}

        </BoxContainer>
      </div>
    );
  });
};

export default accessMobilePage;
